import {
  BookingCalendarSteps,
  EventTimeZoneTypeEnum,
} from 'common/components/entities/Calendar/constants'
import { useEvent } from 'common/components/entities/Calendar/hooks/use-event'
import { getStartEndDates } from 'common/components/entities/Calendar/utils'
import { getLocalTimeZone } from 'common/utils/date-utils'
import {
  getActualBookingEvent,
  getEvent,
} from 'publisher/api/booking-calendar-api'
import { useEventTimeSlots } from 'publisher/hooks/use-event-time-slots'
import { useEffect, useState } from 'react'
import { useCalculateTypography } from 'src/publisher/shared/hooks/use-calculate-typography'
import { useActualBookingEvent } from './hooks/use-actual-booking-event'
import { usePaidCalendarSelectors } from './use-paid-calendar-selectors'

export const useBookingPaidCalendarParams = () => {
  const [activeStep, setActiveStep] = useState(
    BookingCalendarSteps.CalendarStep,
  )

  const {
    selectedDate,
    selectedTimeSlot,
    selectedLocationId,
    selectedTimeZone,
    handleChangeField,
  } = usePaidCalendarSelectors()

  const {
    actualEventId,
    isFetching: isActualEventFetching,
    actualEventError,
  } = useActualBookingEvent({
    fetcher: getActualBookingEvent,
  })
  const {
    event,
    isFetching: isEventFetching,
    error: eventError,
  } = useEvent({
    eventId: actualEventId || null,
    fetcher: getEvent,
  })
  const { timeSlots = [], isFetching: isTimeSlotsFetching } = useEventTimeSlots(
    { isPaidCalendar: true },
  )

  useEffect(() => {
    if (event) {
      handleChangeField('eventId', event.id)
      handleChangeField('ownerId', event.ownerId)
      handleChangeField('eventLocationId', event.locations?.[0]?.id)
      handleChangeField(
        'timeZone',
        event.displayTimezoneType === EventTimeZoneTypeEnum.Local
          ? getLocalTimeZone()
          : event.displayTimezone || getLocalTimeZone(),
      )
    }
  }, [event])

  const onMonthSelect = (month: Date) => {
    const { startDate, endDate } = getStartEndDates(month)
    handleChangeField('startDate', startDate)
    handleChangeField('endDate', endDate)
    handleChangeField('date', '')
    handleChangeField('timeSlot', '')
  }

  const { calculatedFontSize, calculatedFontFamily } = useCalculateTypography()

  // Keep loading true during the entire chain:
  // 1. actualEvent is fetching
  // 2. event is fetching
  // 3. actualEventId exists but event hasn't loaded yet (gap between fetches)
  const isEventLoading =
    isActualEventFetching ||
    isEventFetching ||
    (!!actualEventId && !event && !eventError)

  return {
    activeStep,
    setActiveStep,
    selectedDate,
    selectedTimeSlot,
    selectedTimeZone,
    selectedLocationId,
    handleChangeField,
    event,
    timeSlots: timeSlots,
    isTimeSlotsFetching: isTimeSlotsFetching,
    isEventLoading,
    onMonthSelect,
    globalFontSize: calculatedFontSize.desktop,
    globalMobileFontSize: calculatedFontSize.mobile,
    globalFontFamily: calculatedFontFamily.desktop,
    actualEventError,
  }
}
